import { Link } from 'react-router-dom';
import { blogArticles } from '@/mocks/blog-articles';

export default function BlogListingPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://itaobuy.boats';

  const collectionSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "ItaobuySpreadsheet Shopping Guides & Resources",
    "description": "Expert guides, comparisons, and tutorials to help you master product sourcing with ItaobuySpreadsheet. Browse our complete resource library.",
    "url": `${siteUrl}/blog`,
    "hasPart": blogArticles.map((article) => ({
      "@type": "Article",
      "headline": article.title,
      "url": `${siteUrl}/article/${article.slug}`,
      "description": article.metaDescription,
      "author": {
        "@type": "Person",
        "name": article.author
      }
    }))
  };

  return (
    <div className="min-h-screen bg-background-50">
      {/* Schema.org CollectionPage */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(collectionSchema) }}
      />
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50/98 backdrop-blur-md border-b border-secondary-200/60">
        <div className="w-full px-6 md:px-10">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="text-foreground-950 font-bold text-xl tracking-tight whitespace-nowrap hover:text-primary-600 transition-colors duration-200">
              itaobuy
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/" className="text-sm text-foreground-700 hover:text-foreground-950 transition-colors duration-200 whitespace-nowrap">
                Home
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center px-5 py-2 rounded-[10px] bg-foreground-950 text-background-50 text-sm font-medium hover:bg-foreground-800 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                buy it now
              </a>
            </div>
          </div>
        </div>
      </nav>

      <main className="pt-24">
        {/* Header */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="w-full px-6 md:px-10 text-center">
            <span className="inline-block px-4 py-1.5 rounded-full border border-secondary-300 text-xs font-medium text-foreground-600 mb-6">
              Shopping Guide & Resources
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground-950 leading-tight">
              ItaobuySpreadsheet
              <br />
              Complete Resource Library
            </h1>
            <p className="mt-4 text-foreground-600 text-base max-w-xl mx-auto">
              Expert guides, comparisons, and tutorials to help you master product sourcing with ItaobuySpreadsheet.
            </p>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="py-12 md:py-16 bg-white">
          <div className="w-full px-6 md:px-10">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {blogArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group block rounded-2xl bg-background-50 border border-secondary-200 overflow-hidden hover:border-secondary-300 transition-all duration-300 cursor-pointer"
                >
                  {/* Image */}
                  <div className="h-48 overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  {/* Content */}
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="px-2.5 py-0.5 rounded-full bg-accent-100 text-accent-800 text-xs font-medium">
                        {article.category}
                      </span>
                      <span className="text-xs text-foreground-500">{article.date}</span>
                    </div>
                    <h3 className="text-base font-bold text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">
                      {article.metaDescription}
                    </p>
                    <div className="mt-4 flex items-center gap-2">
                      <span className="text-xs font-medium text-primary-500 group-hover:text-primary-600 transition-colors duration-200">
                        Read Article →
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="w-full px-6 md:px-10 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">
              Ready to Start Sourcing Smarter?
            </h2>
            <p className="mt-3 text-foreground-600 text-base max-w-lg mx-auto">
              Access the full ItaobuySpreadsheet system and transform your product sourcing workflow.
            </p>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center mt-6 px-8 py-3.5 rounded-full bg-foreground-950 text-background-50 font-semibold text-sm hover:bg-foreground-800 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              GET ITAOBUY SHEET NOW
              <i className="ri-arrow-right-up-line ml-2"></i>
            </a>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-foreground-950 pt-12 pb-6">
        <div className="w-full px-6 md:px-10 text-center">
          <h3 className="text-xl font-bold text-white mb-4">itaobuy</h3>
          <div className="flex items-center justify-center gap-6 mb-6">
            <Link to="/" className="text-white/50 text-sm hover:text-white/80 transition-colors duration-200 whitespace-nowrap">
              Back to Home
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="text-white/50 text-sm hover:text-white/80 transition-colors duration-200 whitespace-nowrap"
            >
              itaobuy sheet
            </a>
          </div>
          <div className="border-t border-white/10 pt-6">
            <p className="text-white/40 text-xs">
              © 2026 itaobuy.boats. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}